import React, { useContext, useEffect, useRef } from 'react';
import classNames from 'classnames';
import ESIInclude from '../../../../esi';
import styles from './FlyoutMenu.scss';
import { FlyoutMenuItem } from '../flyout-menu-item/FlyoutMenuItem';
import { notifyAnalytics } from '../notify-analytics';
import { isMonetateGroup } from '../../../utils/monetate/isMonetateGroup';
import { EnvVarsFeaturesContext } from '../../../scaffold/EnvVarsFeaturesContext';
import { formatTestId } from '../../../utils/test-id/format-testid';
import { CloseButton } from './CloseButton';
import { Image } from '../Image/Image';
import { allChildrenHaveBrandLogos } from '../../utils/utils';
import { filterMenuData } from '../../../utils/menu/filterMenuData';

type FlyoutMenuProps = {
  url?: string;
  contentId?: string;
  columns: MenuItem[];
  featuredContent?: FeaturedContent[];
  hierarchy: string;
  onClose: () => void;
  focusOnMount?: boolean;
  onFocusMountHandled?: () => void;
  onTabLoop?: () => void;
  onEscape?: () => void;
  isTouch?: boolean;
  withPanel?: boolean;
  displayAllCategoryLink?: boolean;
  isWide?: boolean;
};

export const FlyoutMenu = ({
  url = '',
  contentId = '',
  columns,
  featuredContent,
  hierarchy,
  onClose,
  focusOnMount = false,
  onFocusMountHandled = () => {},
  onTabLoop = null,
  onEscape = () => {},
  isTouch = false,
  displayAllCategoryLink = false,
  isWide = false,
}: FlyoutMenuProps) => {
  const flyoutRef = useRef<HTMLDivElement | null>(null);
  const { features } = useContext(EnvVarsFeaturesContext);

  const MAX_COLUMNS = 6;
  const SHORT_LIST = 5;
  const LONG_LIST = 14;

  const filteredColumns =
    filterMenuData(columns, ['FLYOUT'])
      ?.map(column => ({
        ...column,
        child: filterMenuData(column.child, ['FLYOUT']),
      }))
      ?.filter(x => x.child?.length) || [];

  if (filteredColumns.length === 0) {
    return null;
  }

  const nonEmptyColumns = filteredColumns.filter(
    column => column.child?.length > 0
  );

  const longColumns = [
    nonEmptyColumns[0]?.longColumnOnFlyout || false,
    nonEmptyColumns[1]?.longColumnOnFlyout || false,
    nonEmptyColumns[2]?.longColumnOnFlyout || false,
  ];

  const longColumnCount = longColumns.filter(Boolean).length;

  const columnsToDisplay = nonEmptyColumns.slice(
    0,
    MAX_COLUMNS - longColumnCount
  );

  const isFullWidthNav = isMonetateGroup(features, 'full-width-nav');

  const showSelectedLines = columnsToDisplay.some(column =>
    column.name.endsWith('*')
  );

  const handleFlyoutKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onEscape();
    }
  };

  const featuredLinks =
    featuredContent?.filter(
      item => item?.display && item?.imageUrl && item?.targetUrl
    ) || [];

  const showFeaturedContent = featuredLinks.length >= 4;

  useEffect(() => {
    if (!focusOnMount) {
      return;
    }

    const firstTabbable = flyoutRef.current?.querySelector(
      '#desktop-flyout-menu-columns a[href], #desktop-flyout-menu-columns button'
    ) as HTMLElement | null;

    firstTabbable?.focus();
    onFocusMountHandled();
  }, [focusOnMount, onFocusMountHandled]);

  if (columns.length === 0) {
    return null;
  }

  return (
    <div
      ref={flyoutRef}
      id="desktop-flyout-menu"
      className={classNames(styles.flyoutMenu, {
        [styles.flyoutMenu__fullWidth]: isFullWidthNav,
        [styles.flyoutMenu__wide]: isWide,
      })}
      data-testid="desktop-flyout-menu"
      onKeyDown={handleFlyoutKeyDown}
    >
      {onTabLoop && (
        <button
          type="button"
          className={styles.tabLoopSentinel}
          data-testid="desktop-flyout-tab-loop-sentinel-start"
          onFocus={onTabLoop}
          aria-label="Return to current panel item"
        />
      )}
      <div
        id="desktop-flyout-menu-columns"
        className={classNames(styles.flyoutMenuColumns, {
          [styles.flyoutMenuColumns__wide]: isWide,
        })}
        data-testid="desktop-flyout-menu-columns"
      >
        {showFeaturedContent && (
          <div
            className={classNames(
              styles.flyoutMenuColumn,
              styles.featuredColumn
            )}
            data-testid="desktop-flyout-featured-column"
          >
            <ul className={styles.featuredContentList}>
              {featuredLinks.map((item, index) => (
                <li key={`${item.display}-${index}`}>
                  <a
                    href={item.targetUrl}
                    className={styles.featuredLink}
                    data-testid={`desktop-flyout-featured-link-${index + 1}`}
                    onClick={() =>
                      notifyAnalytics(`${hierarchy}|${item.display}`)
                    }
                  >
                    <Image
                      width={60}
                      height={60}
                      label={item.display}
                      url={item.imageUrl}
                    />
                    <span>{item.display}</span>
                  </a>
                </li>
              ))}
              {displayAllCategoryLink && url && (
                <li className={styles.categoryLink}>
                  <a href={url} onClick={() => notifyAnalytics(hierarchy)}>
                    All {hierarchy}
                  </a>
                </li>
              )}
            </ul>
          </div>
        )}

        {columnsToDisplay.map((column, i) => {
          const links = column.child.slice(
            0,
            column.longColumnOnFlyout ? LONG_LIST : SHORT_LIST
          );

          const linksAllHaveBrandLogos = allChildrenHaveBrandLogos(links);
          return (
            <div
              key={column.name}
              className={classNames(styles.flyoutMenuColumn, {
                [styles.flyoutMenuColumn__long]: longColumns[i],
                [styles[`flyoutMenuColumn__long__${i + 1}`]]: longColumns[i],
              })}
              data-testid={`desktop-flyout-column-${i + 1}`}
            >
              <h4
                className={styles.flyoutMenuTitle}
                data-testid={formatTestId(column.name, 'flyout-heading-')}
                aria-label={
                  column.name.endsWith('*')
                    ? `${column.name.replace('*', '')} selected lines only`
                    : column.name
                }
              >
                {column.name}
              </h4>

              <ul
                className={classNames(styles.flyoutMenuLinks, {
                  [styles.flyoutMenuLinks__brandLogos]: linksAllHaveBrandLogos,
                })}
              >
                {links.map(link => {
                  const { name, url, imageUrl } = link;
                  return (
                    <FlyoutMenuItem
                      key={name}
                      label={name}
                      url={url}
                      imageUrl={imageUrl}
                      hierarchy={`${hierarchy}|${column.name}`}
                    />
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
      {onTabLoop && (
        <button
          type="button"
          className={styles.tabLoopSentinel}
          data-testid="desktop-flyout-tab-loop-sentinel-end"
          onFocus={onTabLoop}
          aria-label="Return to current panel item"
        />
      )}

      {showSelectedLines && (
        <span className={styles.selectedLines} aria-label="Selected lines only">
          * Selected lines
        </span>
      )}

      {contentId && (
        <div
          className={classNames(styles.flyoutMenuBanner, {
            [styles.flyoutMenuBanner__fullWidth]: isFullWidthNav,
          })}
          data-testid="navigation-content"
        >
          <ESIInclude
            key={contentId}
            src={`/esi/online-adverts/banner/flyout?category=${contentId}`}
            id={`navigation-content-${contentId}`}
          />
        </div>
      )}
      {isTouch && <CloseButton onClose={onClose} />}
    </div>
  );
};
